<template>
  <v-skeleton-loader
    v-if="!dataset"
    class="mr-2 mt-6"
    type="list-item-three-line"
  ></v-skeleton-loader>
  <v-form v-else ref="dataset_form">
    <v-row>
      <v-col cols="12" md="12" v-if="noticeLines.length > 0">
        <v-alert dense text :type="noticeType" class="mb-0">
          <div v-for="(line, index) in noticeLines" :key="index">{{ line }}</div>
        </v-alert>
      </v-col>

      <v-col cols="12" md="12">
        <v-file-input
          outlined
          v-model="file"
          accept=".csv,.json,.xlsx,.parquet"
          @change="handleFileUpload"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_file')"
          :rules="fileInputRules"
          :hint="$vuetify.lang.t('$vuetify.gpt_dataset.encoding_hint')"
          persistent-hint
          clearable
          prepend-icon="mdi-paperclip"
          :error-messages="fileErrorMessages"
        ></v-file-input>
      </v-col>

      <v-col cols="12" md="12" v-if="isProcessing">
        <v-skeleton-loader class="mr-2 mt-6" type="list-item-three-line"></v-skeleton-loader>
        <div class="text-center mt-2">
          <span class="text-body-2">{{ $vuetify.lang.t('$vuetify.gpt_dataset.processing_file') }}</span>
        </div>
      </v-col>

      <v-col cols="12" md="12" v-if="df && !isProcessing && !fileErrorMessages.length">
        <v-card outlined class="pa-3">
          <div class="d-flex justify-space-between align-center mb-3">
            <div>
              <div class="text-h6">{{ file.name }}</div>
              <div class="text-body-2 grey--text">
                {{ fileStats.totalRows }} {{ $vuetify.lang.t('$vuetify.gpt_dataset.rows') }} × {{ fileStats.totalColumns }} {{ $vuetify.lang.t('$vuetify.gpt_dataset.column') }}
              </div>
            </div>
            <div>
              <v-btn small outlined color="primary" class="mr-2" @click="showPreviewModal = true">
                <v-icon small class="mr-1">mdi-eye</v-icon>
                {{ $vuetify.lang.t('$vuetify.gpt_dataset.preview') }}
              </v-btn>
              <v-btn small outlined color="info" @click="showStatsModal = true">
                <v-icon small class="mr-1">mdi-chart-bar</v-icon>
                {{ $vuetify.lang.t('$vuetify.gpt_dataset.stats') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
      <!-- <v-col cols="12" md="12" v-if="df && !isProcessing && !fileErrorMessages.length">
        <v-autocomplete 
          v-model="selectedDatetimeColumns" 
          :items="selectedDatetimeColumns"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.datetime_columns')" 
          :disabled="isUpdateMode"
          :rules="datetimeColumnsRule"
          multiple
          small-chips
          deletable-chips
          outlined
          append-icon="mdi-menu-down"
        >
          <template v-slot:append>
            <div class="append-icons">
              <v-tooltip right max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.gpt_dataset.datetime_columns_hint') }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" md="12" v-if="selectedDatetimeColumns.length > 0">
        <v-autocomplete 
          v-model="selectedPrimaryDatetimeColumn"
          :items="selectedDatetimeColumns"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_column')"
          outlined
          :disabled="!df"
          :rules="[primaryDatetimeRule]"
          >
          <template v-slot:append>
            <div class="append-icons">
              <v-icon>mdi-menu-down</v-icon>
              <v-tooltip right max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_column_hint') }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-autocomplete>
      </v-col> -->

      <v-col cols="12" class="d-flex justify-end pr-6">
        <v-btn
          :loading="uploadBtnLoader"
          outlined
          @click="uploadDataset"
          color="primary"
        >
          {{ $vuetify.lang.t('$vuetify.common.upload_btn') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="showPreviewModal" max-width="900px">
      <v-card>
        <v-card-title class="text-h5">
          {{ $vuetify.lang.t('$vuetify.gpt_dataset.file_preview') }}
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
          <div class="mb-3">
            <strong>{{ $vuetify.lang.t('$vuetify.gpt_dataset.file') }}:</strong> {{ file ? file.name : '' }} |
            <strong>{{ $vuetify.lang.t('$vuetify.gpt_dataset.rows') }}:</strong> {{ fileStats.totalRows }} |
            <strong>{{ $vuetify.lang.t('$vuetify.gpt_dataset.column') }}:</strong> {{ fileStats.totalColumns }}
          </div>
          <div class="preview-table-container">
            <v-simple-table fixed-header height="384px">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th v-for="header in filePreview?.headers" :key="header" class="text-left">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in filePreview?.rows" :key="index">
                    <td v-for="(value, colIndex) in row" :key="colIndex">
                      {{ formatPreviewValue(value) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 modal-footer">
          <v-spacer></v-spacer>
          <v-btn text @click="showPreviewModal = false" class="mr-2 red--text">
            {{ $vuetify.lang.t('$vuetify.common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showStatsModal" max-width="1000px">
      <v-card style="max-height: 60vh; display: flex; flex-direction: column;">
        <v-card-title class="text-h5" style="flex-shrink: 0;">
          {{ $vuetify.lang.t('$vuetify.gpt_dataset.column_statistics') }}
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text style="display: flex; flex-direction: column; flex-grow: 1; overflow-y: hidden;">
          <div class="stats-table-container">
            <v-simple-table fixed-header height="384px">
              <template v-slot:default>
                <thead>
                  <tr>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.column') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.type') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.null_count') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.unique_values') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.sample_values') }}</th>
                </tr>
                </thead>
                <tbody>
                  <tr v-for="stat in columnStats" :key="stat.column">
                    <td><strong>{{ stat.column }}</strong></td>
                    <td>
                      <v-chip small :color="getTypeColor(stat.inferredType)">
                        {{ stat.inferredType }}
                      </v-chip>
                    </td>
                    <td>{{ stat.nullCount }} ({{ stat.nullPercentage }}%)</td>
                    <td>{{ stat.uniqueCount }}</td>
                    <td class="sample-values">{{ stat.sampleValues.join(', ') }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
        <v-card-actions style="flex-shrink: 0;">
          <v-spacer></v-spacer>
          <v-btn text @click="showStatsModal = false" class="mr-2 red--text">
            {{ $vuetify.lang.t('$vuetify.common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>

import { mapActions, mapState } from "vuex";
import {
  ADD_ALERT,
  UPDATE_GPT_DATASET_TS_FILE,
  GET_GPT_DATASET_BY_ID,
  GET_GPT_DATASETS_SUMMARY
} from "@/store/_actiontypes";
import {
  processFile,
  generateFilePreview,
  generateColumnStats,
  formatPreviewValue,
  getTypeColor
} from "@/helpers/helper_ts";
import {
  effectiveTimezone,
  policyNoticeLines,
  readDatetimeFields,
  readTimezonePolicies,
} from "@/helpers/timezone";

export default {
  name: "UploadDatasetTsModal",
  props: {
    showModal: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    showModal(newVal) {
      this.localShowModal = newVal;
    },
  },
  computed: {
    ...mapState({
      dataset: state => state.project.selectedGptDataset,
      gptSetupState: state => state.project.gptSetupState,
      isUpdateMode() {
        return this.datetimeFields.length > 0;
      },
      datetimeFields() {
        return readDatetimeFields(this.dataset);
      },
      timezonePolicies() {
        return readTimezonePolicies(this.dataset);
      },
      noticeType() {
        return this.timezonePolicies.naivePolicy ? "info" : "warning";
      },
      // What the dataset's stored policies will do to this file's values.
      // Stated before the upload because it cannot be undone afterwards. The
      // policies themselves are edited in the dataset form, not here.
      noticeLines() {
        if (this.datetimeFields.length === 0) return [];
        const t = key => this.$vuetify.lang.t(`$vuetify.gpt_dataset.${key}`);
        const { naivePolicy, awarePolicy } = this.timezonePolicies;
        const mappings = this.datetimeFields
          .map(field => {
            const zone = effectiveTimezone(field, this.dataset);
            return zone ? `${field.name} → ${zone}` : null;
          })
          .filter(Boolean);
        return policyNoticeLines({ t, naivePolicy, awarePolicy, mappings });
      },
      fileInputRules() {
        let rules = [];
        
        if (!this.gptSetupState?.dataset?.completed) {
          rules.push(
            value => !!value || this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_file") +
            this.$vuetify.lang.t("$vuetify.validation.required_error_msg_txt"),
          );
        }
        rules.push(
          value => !value || value.size <= this.MAX_FILE_SIZE ||
          `${this.$vuetify.lang.t('$vuetify.gpt_dataset.file_size_exceeded_error')} ${this.MAX_FILE_SIZE / 1024 / 1024}MB`
        );
        return rules;
      },
      datetimeColumnsRule() {
        return [
          v => !!v && v.length > 0 || this.$vuetify.lang.t('$vuetify.gpt_dataset.datetime_fields_required')
        ];
      },
      primaryDatetimeRule() {
        return !!this.selectedPrimaryDatetimeColumn || this.$vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_field_required');
      },
      fileErrorMessages() {
        const messages = [];
        if (this.errorMessage) {
          messages.push(this.errorMessage);
        }
        return messages;
      }
    }),
  },
  data() {
    return {
      file: null,
      df: null,
      isProcessing: false,
      uploadBtnLoader: false,
      errorMessage: null,
      filePreview: null,
      selectedPrimaryDatetimeColumn: null,
      localShowModal: this.showModal,
      fileStats: {
        totalRows: 0,
        totalColumns: 0
      },
      columnStats: [],
      showPreviewModal: false,
      showStatsModal: false,
      selectedDatetimeColumns: [],
      MAX_FILE_SIZE: 100 * 1024 * 1024,
      MAX_COLUMNS: 1000,
      MIN_ROWS: 1,
      SAMPLE_SIZE: 100,
      PREVIEW_ROWS: 5,
    };
  },
  methods: {
    ...mapActions("project", [UPDATE_GPT_DATASET_TS_FILE, GET_GPT_DATASETS_SUMMARY, GET_GPT_DATASET_BY_ID]),
    formatPreviewValue,
    getTypeColor,

    validateFile() {
      if (!this.file) {
        throw new Error(this.$vuetify.lang.t('$vuetify.gpt_dataset.no_file_selected'));
      }

      if (this.file.size > this.MAX_FILE_SIZE) {
        throw new Error(`${this.$vuetify.lang.t('$vuetify.gpt_dataset.file_size_exceeded_error')} ${this.MAX_FILE_SIZE / 1024 / 1024}MB`);
      }

      const allowedExtensions = ['csv', 'json', 'xlsx', 'parquet'];
      const fileExtension = this.file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        throw new Error(this.$vuetify.lang.t('$vuetify.gpt_dataset.unsupported_file_error') + ` ${fileExtension}`);
      }
    },
    validateDataset() {
      if (!this.df || this.df.shape[0] < this.MIN_ROWS) {
        throw new Error( 
          this.$vuetify.lang.t('$vuetify.gpt_dataset.min_column_error', { count: this.MIN_ROWS }) + ` Current: ${this.df ? this.df.shape[0] : 0}`);
      }

      if (this.df.shape[1] > this.MAX_COLUMNS) {
        this.$vuetify.lang.t('$vuetify.gpt_dataset.max_column_error') + ` (${this.df.shape[1]}).` +
          this.$vuetify.lang.t('$vuetify.gpt_dataset.max_column_allowed', { count: this.MAX_COLUMNS })
      }

      this.fileStats = {
        totalRows: this.df.shape[0],
        totalColumns: this.df.shape[1]
      };
    },
    validateDatetimeFields() {
      if (!this.isUpdateMode) return;
      
      const missingFields = this.datetimeFields
        .map(field => field.name)
        .filter(name => !this.df.columns.includes(name));

      if (missingFields.length > 0) {
        throw new Error(
          `${this.$vuetify.lang.t('$vuetify.gpt_dataset.missing_datetime_column')} ${missingFields.join(', ')}`
        );
      }
    },
    resetState() {
      this.df = null;

      this.filePreview = null;
      this.fileStats = { totalRows: 0, totalColumns: 0 };
      this.columnStats = [];
      this.showPreviewModal = false;
      this.showStatsModal = false;

      this.selectedDatetimeColumns = [];
      this.selectedPrimaryDatetimeColumn = null;
      this.isProcessing = false;
      this.errorMessage = null;
    },
    async handleFileUpload() {
      this.resetState();

      if (!this.file) return;

      try {
        this.validateFile();
        this.isProcessing = true;
        this.df = await processFile(this.file);

        if (!this.df) {
          throw new Error('Failed to create DataFrame from file');
        }

        this.validateDataset();
        this.validateDatetimeFields();

        this.filePreview = generateFilePreview(this.df, this.PREVIEW_ROWS);
        const { columnStats, datetimeColumns } = generateColumnStats(this.df, this.SAMPLE_SIZE);
        
        this.columnStats = columnStats;
        
        if (this.isUpdateMode) {
          this.selectedDatetimeColumns = this.datetimeFields.map(field => field.name);
          this.selectedPrimaryDatetimeColumn = this.dataset.primary_datetime_field;
        }
      } catch (err) {
        this.errorMessage = err.message;
      } finally {
        this.isProcessing = false;
      }
    },
    uploadDataset() {
      if (!this.$refs.dataset_form.validate()) return;
      if(!this.file || this.isProcessing || this.fileErrorMessages.length > 0) return;
      this.uploadBtnLoader = true;
      this.UPDATE_GPT_DATASET_TS_FILE({
        project_id: this.$route.query.pid,
        datasetId: this.$route.query.datasetId,
        datasetFile: this.file,
      })
        .then(res => {
          // MINIMAL CHANGE: Emit task started event instead of starting monitoring here
          this.$emit('task-started', { taskId: res.data.detail.task_id });
          this.uploadBtnLoader = false;
        })
        .catch(err => {
          console.error("Upload error:", err);
          this.uploadBtnLoader = false;
          if (err.response?.status === 400 &&
            err.response?.data?.detail?.error?.includes("Invalid encoding")) {
            const errorMessage = this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error");
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: errorMessage,
                color: "error",
              },
              { root: true },
            );
          }
        });
    },
    closeModal() {
      this.localShowModal = false;
      this.$emit('close-modal');
    },
    uploadSuccess() {
      this.$emit('upload-success');
    }
  }
};
</script>

<style scoped>
.append-icons {
  display: flex;
  align-items: center;
  gap: 4px;
}

.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
}

.preview-table-container {
  max-height: 60vh;
  overflow-y: auto;
}

.stats-table-container {
  max-height: 50vh;
  overflow-y: auto;
}

.sample-values {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>